import { View, StyleSheet, Pressable, Linking, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ShiftLocationType } from "@/types/db/shifts";

interface LocationTabProps {
  location?: ShiftLocationType;
  projectLocation?: ShiftLocationType;
}

export function LocationTab({ location, projectLocation }: LocationTabProps) {
  const { colors } = useTheme();

  // Use shift location first, fall back to project location
  const displayLocation = location || projectLocation;
  const isFromProject = !location && !!projectLocation;

  if (!displayLocation) {
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="map-marker-off" size={48} color={colors.placeholder} />
        <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>No location set</ThemedText>
      </View>
    );
  }

  const address = displayLocation.address;
  const hasAddress = address && (address.street || address.city || address.state || address.zip);
  const fullAddress = hasAddress
    ? `${address.street || ""}, ${address.city || ""}, ${address.state || ""} ${address.zip || ""}`.trim()
    : "";

  // Format for display: "123 Main St, City, ST 12345"
  const displayAddress = hasAddress
    ? [address.street, [address.city, address.state].filter(Boolean).join(", "), address.zip].filter(Boolean).join(", ")
    : "";

  const handleOpenMaps = async () => {
    if (!fullAddress) return;

    const encodedAddress = encodeURIComponent(fullAddress);

    // Try platform-specific maps first, fallback to Google Maps
    let url: string;
    if (Platform.OS === "ios") {
      url = `maps://maps.apple.com/?address=${encodedAddress}`;
    } else {
      url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }

    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
    } else {
      // Fallback to Google Maps web
      await Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`);
    }
  };

  const canOpenMaps = !!fullAddress;

  return (
    <View style={styles.container}>
      {/* Location - clickable row */}
      <Pressable
        onPress={canOpenMaps ? handleOpenMaps : undefined}
        style={({ pressed, hovered }) => [
          styles.locationRow,
          { cursor: canOpenMaps ? "pointer" : "default" } as any,
          hovered && canOpenMaps && { backgroundColor: colors.backgroundSecondary },
          pressed && canOpenMaps && styles.pressed,
        ]}
      >
        <View style={styles.locationContent}>
          <View style={styles.nameRow}>
            <ThemedText style={styles.locationName}>{displayLocation.name}</ThemedText>
            {isFromProject && (
              <View
                style={[styles.badge, { backgroundColor: colors.cyan?.background, borderColor: colors.cyan?.border }]}
              >
                <ThemedText style={[styles.badgeText, { color: colors.cyan?.text }]}>From Project</ThemedText>
              </View>
            )}
          </View>
          {displayAddress && (
            <ThemedText style={[styles.addressText, { color: colors.textSecondary }]}>{displayAddress}</ThemedText>
          )}
        </View>
        {canOpenMaps && <MaterialCommunityIcons name="directions" size={22} color={colors.primary} />}
      </Pressable>

      {/* Instructions section */}
      {displayLocation.instructions && (
        <>
          <View style={[styles.divider, { backgroundColor: colors.border }]} />
          <View style={styles.instructionsSection}>
            <View style={styles.instructionsHeader}>
              <MaterialCommunityIcons name="information-outline" size={14} color={colors.textSecondary} />
              <ThemedText style={[styles.instructionsLabel, { color: colors.textSecondary }]}>Instructions</ThemedText>
            </View>
            <ThemedText style={[styles.instructionsText, { color: colors.text }]}>
              {displayLocation.instructions}
            </ThemedText>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  locationRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: 12,
    marginHorizontal: -12,
    borderRadius: 8,
  },
  locationContent: {
    flex: 1,
    gap: 4,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  locationName: {
    fontSize: 16,
    fontWeight: "600",
  },
  addressText: {
    fontSize: 14,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "600",
  },
  pressed: {
    opacity: 0.7,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
  },
  instructionsSection: {
    gap: 6,
  },
  instructionsHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  instructionsLabel: {
    fontSize: 12,
    fontWeight: "500",
  },
  instructionsText: {
    fontSize: 14,
    lineHeight: 20,
  },
});
